import type { ReviewComment } from "@/components/ai/review/types";
import { formatReviewCommentsForAgent } from "@/components/ai/review/use-review-comments";
import { useAgentAction } from "@/hooks/use-agent-action";
import { useCallback } from "react";

type UseSendCommentsToAgentOptions = {
    /** Remove a single comment by ID — called after a successful single-comment send. */
    removeComment?: (commentId: string) => void;
    /** Remove all comments — called after a successful send-all. */
    clearAllComments?: () => void;
};

/**
 * Wraps useAgentAction with comment-specific helpers. Handles formatting,
 * sending, error handling, and comment cleanup after successful sends.
 */
export function useSendCommentsToAgent(
    environmentId: string,
    { removeComment, clearAllComments }: UseSendCommentsToAgentOptions,
) {
    const { isAgentBusy, isPending, sendAgentMessage, showError } = useAgentAction(environmentId);

    /** Format and send a single review comment, then remove it. */
    const sendComment = useCallback(
        async (comment: ReviewComment) => {
            try {
                const formatted = formatReviewCommentsForAgent([comment]);
                await sendAgentMessage(formatted);
                removeComment?.(comment.id);
            } catch (error) {
                showError("Failed to send comment", error);
            }
        },
        [sendAgentMessage, showError, removeComment],
    );

    /** Format and send all review comments, then clear them. */
    const sendAllComments = useCallback(
        async (comments: ReviewComment[]) => {
            if (comments.length === 0) return;
            try {
                const formatted = formatReviewCommentsForAgent(comments);
                await sendAgentMessage(formatted);
                clearAllComments?.();
            } catch (error) {
                showError("Failed to send comments", error);
            }
        },
        [sendAgentMessage, showError, clearAllComments],
    );

    return { isAgentBusy, isPending, sendComment, sendAllComments };
}
